import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Calendar, Clock, Tag, School, AlertCircle, Sparkles, ChevronDown, FileText, Target, DollarSign, User, BookOpen, CheckSquare, Ban, Link } from 'lucide-react';
import { format, addDays, startOfWeek, isToday, isTomorrow, isThisWeek } from 'date-fns';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import timelineService from '../../services/timelineService';
// import { useNavigate } from 'react-router-dom'; // Will be used when task cards have action buttons

// Smart suggestions for task categorization
const TASK_KEYWORDS = {
  essay: ['essay', 'write', 'draft', 'revision', 'personal statement', 'supplement', 'edit'],
  research: ['research', 'visit', 'tour', 'explore', 'find', 'college', 'school', 'university'],
  testing: ['test', 'sat', 'act', 'ap', 'exam', 'study', 'prep', 'practice'],
  financial: ['fafsa', 'css', 'scholarship', 'financial', 'aid', 'grant', 'loan'],
  admin: ['submit', 'application', 'apply', 'deadline', 'form', 'transcript', 'recommendation'],
  profile: ['activity', 'resume', 'brag sheet', 'achievement', 'extracurricular', 'portfolio']
};

// Module routes mapping
const MODULE_ROUTES = {
  'Timeline': '/college/timeline',
  'Essays': '/college/essays',
  'Colleges': '/college/colleges',
  'Profile': '/college/profile',
  'Brag Sheet': '/college/brag-sheet',
  'Assessment': '/college/assessment',
  'Financial Aid': '/college/financial-aid'
};

// Module icons
const MODULE_ICONS = {
  'Timeline': CheckSquare,
  'Essays': FileText,
  'Colleges': School,
  'Profile': User,
  'Brag Sheet': BookOpen,
  'Assessment': Target,
  'Financial Aid': DollarSign
};

const QuickTaskAdd = ({
  date,
  currentUser,
  colleges = [],
  onTaskAdded,
  existingTasks = [],
  currentWorkload = 0,
  position = 'inline' // 'inline', 'floating', or 'modal'
}) => {
  // const navigate = useNavigate(); // Will be used when task cards have action buttons
  const [isAdding, setIsAdding] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [mode, setMode] = useState('quick'); // 'quick' or 'detailed'
  const [category, setCategory] = useState('admin');
  const [estimatedHours, setEstimatedHours] = useState(1);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [priority, setPriority] = useState('medium');
  const [suggestedCategory, setSuggestedCategory] = useState(null);
  const [suggestedHours, setSuggestedHours] = useState(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadingInsight, setLoadingInsight] = useState('');
  const [selectedDueDate, setSelectedDueDate] = useState(date);
  const [expandLevel, setExpandLevel] = useState('minimal'); // 'minimal', 'smart', 'detailed'
  const [selectedModule, setSelectedModule] = useState(null);
  const [suggestedModule, setSuggestedModule] = useState(null);
  const [tasksForDate, setTasksForDate] = useState([]);
  const [showTasksPreview, setShowTasksPreview] = useState(false);
  const [quickDateOption, setQuickDateOption] = useState('custom');
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [userHasSetHours, setUserHasSetHours] = useState(false);
  const [userHasSetModule, setUserHasSetModule] = useState(false);
  const [customModuleLink, setCustomModuleLink] = useState('');
  const [showCustomLinkInput, setShowCustomLinkInput] = useState(false);

  const inputRef = useRef(null);

  // Loading insights for time-respecting design
  const insights = [
    "Tasks with specific action verbs (research, write, review) get completed 40% more often",
    "Breaking large tasks into 2-hour chunks increases completion rate by 60%",
    "Students who add their own tasks show 30% higher engagement",
    "Tip: Start task titles with verbs like 'Complete', 'Research', or 'Draft' for clarity"
  ];

  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAdding]);

  // Fetch tasks for selected date
  useEffect(() => {
    const fetchTasksForDate = async () => {
      if (!currentUser?.id || !selectedDueDate) return;

      setLoadingTasks(true);
      const { data, error } = await timelineService.getTasksByDate(
        currentUser.id,
        selectedDueDate
      );

      if (!error && data) {
        setTasksForDate(data);
        // Auto-show preview if date has many tasks
        if (data.length > 2) {
          setShowTasksPreview(true);
        }
      }
      setLoadingTasks(false);
    };

    fetchTasksForDate();
  }, [selectedDueDate, currentUser]);

  // Progressive disclosure based on user interaction
  useEffect(() => {
    if (taskTitle.length > 8) {
      setExpandLevel('smart');
    } else if (taskTitle.length === 0) {
      setExpandLevel('minimal');
    }
  }, [taskTitle]);

  // Auto-detect category and module based on keywords
  useEffect(() => {
    if (taskTitle.length > 3) {
      const lowerTitle = taskTitle.toLowerCase();
      for (const [cat, keywords] of Object.entries(TASK_KEYWORDS)) {
        if (keywords.some(keyword => lowerTitle.includes(keyword))) {
          setSuggestedCategory(cat);
          // Auto-suggest duration based on category
          const hourSuggestions = {
            essay: 2,
            research: 1.5,
            testing: 2.5,
            financial: 1,
            admin: 0.75,
            profile: 1
          };
          setSuggestedHours(hourSuggestions[cat]);

          // Auto-suggest module based on category
          const moduleMapping = {
            essay: 'Essays',
            research: 'Colleges',
            testing: 'Assessment',
            financial: 'Financial Aid',
            admin: 'Timeline',
            profile: 'Profile'
          };
          setSuggestedModule(moduleMapping[cat]);
          break;
        }
      }

      // Check for potential duplicates
      const similar = existingTasks.find(t =>
        t.title.toLowerCase().includes(lowerTitle.slice(0, 10)) ||
        lowerTitle.includes(t.title.toLowerCase().slice(0, 10))
      );
      setShowDuplicateWarning(!!similar);
    }
  }, [taskTitle, existingTasks]);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!taskTitle.trim()) return;

    setIsSaving(true);
    // Show productive loading insight
    const randomInsight = insights[Math.floor(Math.random() * insights.length)];
    setLoadingInsight(randomInsight);

    try {
      // Use suggested values only if user hasn't made explicit choices
      const finalCategory = suggestedCategory || category;
      const finalHours = userHasSetHours ? estimatedHours : (suggestedHours || estimatedHours);
      const finalModule = userHasSetModule ? selectedModule : (selectedModule || null); // Don't auto-apply module suggestions

      // Calculate smart defaults
      const taskData = {
        user_id: currentUser.id,
        title: taskTitle.trim(),
        category: finalCategory,
        scheduled_date: format(selectedDueDate, 'yyyy-MM-dd'),
        due_date: format(selectedDueDate, 'yyyy-MM-dd'), // Use selected due date
        estimated_duration: finalHours * 60, // Convert to minutes
        priority: priority, // Use priority value directly since we now use correct values
        college_id: selectedCollege,
        status: 'planned',
        completion_status: 'not_started',
        is_user_created: true,
        created_by_user_at: new Date().toISOString(),
        source_type: 'user_created',
        module_name: finalModule,
        module_link: showCustomLinkInput && customModuleLink ? customModuleLink : (finalModule ? MODULE_ROUTES[finalModule] : null)
      };

      const { data, error } = await supabase
        .from('timeline_items')
        .insert([taskData])
        .select()
        .single();

      if (error) throw error;

      // Success feedback
      toast.success(`Task added! ${currentWorkload + finalHours > 6 ? '⚠️ Consider your workload' : '✨ Great planning!'}`);

      // Reset form
      setTaskTitle('');
      setCategory('admin');
      setEstimatedHours(1);
      setSelectedCollege(null);
      setPriority('medium');
      setSelectedDueDate(date); // Reset to original date
      setIsAdding(false);
      setMode('quick');
      setSuggestedCategory(null);
      setSuggestedHours(null);
      setSuggestedModule(null);
      setSelectedModule(null);
      setExpandLevel('minimal');
      setQuickDateOption('custom');
      setUserHasSetHours(false);
      setUserHasSetModule(false);
      setCustomModuleLink('');
      setShowCustomLinkInput(false);

      // Notify parent component
      if (onTaskAdded) {
        onTaskAdded(data);
      }
    } catch (error) {
      console.error('Error adding task:', error);
      toast.error('Failed to add task');
    } finally {
      setIsSaving(false);
      setLoadingInsight('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Shift+Enter for detailed mode
        e.preventDefault();
        setMode('detailed');
        setExpandLevel('detailed');
      } else if (mode === 'quick') {
        // Enter to submit in quick mode
        e.preventDefault();
        handleSubmit();
      }
    } else if (e.key === 'Escape') {
      setIsAdding(false);
      setMode('quick');
      setExpandLevel('minimal');
    } else if (e.key === 'Tab' && taskTitle.length > 0) {
      // Tab to expand to more options
      e.preventDefault();
      setExpandLevel('detailed');
    }
  };

  // Quick date selection handlers
  const handleQuickDateSelect = (option) => {
    const today = new Date();
    let newDate;

    switch (option) {
      case 'today':
        newDate = today;
        break;
      case 'tomorrow':
        newDate = addDays(today, 1);
        break;
      case 'this-week':
        // Find next available weekday
        newDate = addDays(today, 2);
        while (newDate.getDay() === 0 || newDate.getDay() === 6) {
          newDate = addDays(newDate, 1);
        }
        break;
      case 'next-week':
        newDate = startOfWeek(addDays(today, 7), { weekStartsOn: 1 });
        break;
      default:
        return;
    }

    setSelectedDueDate(newDate);
    setQuickDateOption(option);
  };

  // Get label for quick date option
  const getQuickDateLabel = () => {
    if (isToday(selectedDueDate)) return 'Today';
    if (isTomorrow(selectedDueDate)) return 'Tomorrow';
    if (isThisWeek(selectedDueDate)) return format(selectedDueDate, 'EEEE');
    return format(selectedDueDate, 'MMM d');
  };

  // Workload indicator
  const getWorkloadColor = () => {
    const totalHours = currentWorkload + (suggestedHours || estimatedHours);
    if (totalHours > 6) return 'text-red-600 bg-red-50';
    if (totalHours > 4) return 'text-amber-600 bg-amber-50';
    return 'text-emerald-600 bg-emerald-50';
  };

  // Calculate total hours for selected date
  const getDateWorkload = () => {
    return tasksForDate.reduce((total, task) => {
      return total + (task.estimated_duration || 60) / 60;
    }, 0);
  };

  // Handle explicit user changes
  const handleHoursChange = (hours) => {
    setEstimatedHours(hours);
    setUserHasSetHours(true);
  };

  const handleModuleSelect = (moduleName) => {
    if (moduleName === 'Custom') {
      setShowCustomLinkInput(true);
      setSelectedModule(null);
    } else if (moduleName === 'None') {
      setSelectedModule(null);
      setShowCustomLinkInput(false);
    } else {
      setSelectedModule(moduleName);
      setShowCustomLinkInput(false);
    }
    setUserHasSetModule(true);
  };

  const handleApplyAllSuggestions = () => {
    if (suggestedHours && !userHasSetHours) {
      setEstimatedHours(suggestedHours);
      setUserHasSetHours(true);
    }
    if (suggestedModule && !userHasSetModule) {
      setSelectedModule(suggestedModule);
      setUserHasSetModule(true);
    }
    if (suggestedCategory) {
      setCategory(suggestedCategory);
    }
    toast.success('Applied AI suggestions');
  };

  // For modal position, always show the form
  if (!isAdding && position !== 'modal') {
    return (
      <button
        onClick={() => setIsAdding(true)}
        className={`
          group flex items-center gap-2 w-full transition-all
          ${position === 'inline'
            ? 'p-2 hover:bg-slate-50 rounded-lg'
            : 'fixed bottom-6 right-6 px-4 py-3 bg-teal-600 text-white rounded-full shadow-lg hover:bg-teal-700 hover:scale-105'
          }
        `}
      >
        <Plus className={position === 'inline' ? 'w-4 h-4 text-slate-400' : 'w-5 h-5'} />
        <span className={position === 'inline' ? 'text-sm text-slate-600' : 'font-medium'}>
          {position === 'inline' ? `Add task for ${format(date, 'MMM d')}` : 'Add Task'}
        </span>
      </button>
    );
  }

  // Auto-show form for modal position
  if (position === 'modal' && !isAdding) {
    setIsAdding(true);
  }

  return (
    <div className={`
      ${position === 'inline'
        ? 'border border-stone-200 rounded-lg p-3 bg-white'
        : position === 'modal'
        ? 'p-6 bg-white rounded-xl border border-stone-200'
        : 'fixed bottom-6 right-6 w-96 bg-white rounded-xl shadow-2xl border border-stone-200 p-4'
      }
    `}>
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Header with close button */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Add College Task
          </h3>
          <button
            type="button"
            onClick={() => {
              setIsAdding(false);
              setMode('quick');
              // If floating or modal position, notify parent to close
              if ((position === 'floating' || position === 'modal') && onTaskAdded) {
                onTaskAdded(null); // Pass null to indicate cancellation
              }
            }}
            className="p-1.5 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-stone-400" />
          </button>
        </div>

        {/* Quick capture inputs */}
        <div className="space-y-4">
          {/* Task title */}
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="What needs to be done?"
              className="w-full px-4 py-3 border-2 border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 text-base placeholder-stone-400"
              disabled={isSaving}
            />

            {/* Smart suggestions indicator */}
            {(suggestedCategory || suggestedModule) && !isSaving && expandLevel !== 'minimal' && (
              <div className="absolute right-3 top-3">
                <Sparkles className="w-5 h-5 text-teal-500 animate-pulse" />
              </div>
            )}
          </div>

          {/* Enhanced Date Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700 block">When?</label>

            {/* Quick date options */}
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => handleQuickDateSelect('today')}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  quickDateOption === 'today'
                    ? 'bg-teal-600 text-white'
                    : 'bg-stone-100 text-slate-700 hover:bg-stone-200'
                }`}
                disabled={isSaving}
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => handleQuickDateSelect('tomorrow')}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  quickDateOption === 'tomorrow'
                    ? 'bg-teal-600 text-white'
                    : 'bg-stone-100 text-slate-700 hover:bg-stone-200'
                }`}
                disabled={isSaving}
              >
                Tomorrow
              </button>
              <button
                type="button"
                onClick={() => handleQuickDateSelect('this-week')}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  quickDateOption === 'this-week'
                    ? 'bg-teal-600 text-white'
                    : 'bg-stone-100 text-slate-700 hover:bg-stone-200'
                }`}
                disabled={isSaving}
              >
                This Week
              </button>
              <button
                type="button"
                onClick={() => setQuickDateOption('custom')}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1 ${
                  quickDateOption === 'custom'
                    ? 'bg-teal-600 text-white'
                    : 'bg-stone-100 text-slate-700 hover:bg-stone-200'
                }`}
                disabled={isSaving}
              >
                <Calendar className="w-4 h-4" />
                Custom
              </button>
            </div>

            {/* Date picker (shown when custom is selected) */}
            {quickDateOption === 'custom' && (
              <input
                type="date"
                value={format(selectedDueDate, 'yyyy-MM-dd')}
                onChange={(e) => {
                  setSelectedDueDate(new Date(e.target.value));
                  setQuickDateOption('custom');
                }}
                className="w-full px-3 py-2 border-2 border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 text-sm"
                disabled={isSaving}
              />
            )}

            {/* Selected date display */}
            <div className="text-sm text-slate-600 bg-stone-50 px-3 py-2 rounded-lg">
              Selected: <span className="font-medium text-slate-900">{getQuickDateLabel()}</span>
              {tasksForDate.length > 0 && !loadingTasks && (
                <button
                  type="button"
                  onClick={() => setShowTasksPreview(!showTasksPreview)}
                  className="ml-2 text-teal-600 hover:text-teal-700 font-medium inline-flex items-center gap-1"
                >
                  {tasksForDate.length} task{tasksForDate.length !== 1 ? 's' : ''} ({getDateWorkload().toFixed(1)}h)
                  <ChevronDown className={`w-3 h-3 transition-transform ${showTasksPreview ? 'rotate-180' : ''}`} />
                </button>
              )}
            </div>
          </div>

          {/* Tasks preview for selected date */}
          {showTasksPreview && tasksForDate.length > 0 && (
            <div className="bg-stone-50 rounded-lg p-3 space-y-2 animate-in slide-in-from-top-2">
              <p className="text-xs font-medium text-slate-700 mb-2">Already scheduled:</p>
              {tasksForDate.slice(0, 3).map((task, idx) => (
                <div key={task.id || idx} className="flex items-center justify-between text-xs">
                  <span className="text-slate-600 truncate flex-1">
                    {task.title}
                  </span>
                  <span className="text-slate-500 ml-2">
                    {((task.estimated_duration || 60) / 60).toFixed(1)}h
                  </span>
                </div>
              ))}
              {tasksForDate.length > 3 && (
                <p className="text-xs text-slate-500 italic">
                  +{tasksForDate.length - 3} more tasks
                </p>
              )}
              <div className={`text-xs font-medium px-2 py-1 rounded ${
                getDateWorkload() > 6 ? 'bg-red-100 text-red-700' :
                getDateWorkload() > 4 ? 'bg-amber-100 text-amber-700' :
                'bg-emerald-100 text-emerald-700'
              }`}>
                Total workload: {getDateWorkload().toFixed(1)} hours
              </div>
            </div>
          )}

          {/* Module selector - shows when smart level or user requested */}
          {(expandLevel === 'smart' || expandLevel === 'detailed') && suggestedModule && (
            <div className="animate-in slide-in-from-top-1">
              <label className="text-sm font-medium text-slate-700 mb-2 block flex items-center gap-2">
                Where to work on this?
                {suggestedModule && (
                  <span className="text-xs text-teal-600 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Suggested: {suggestedModule}
                  </span>
                )}
              </label>
              <div className="space-y-2">
                {/* Main module grid */}
                <div className="grid grid-cols-4 gap-2">
                  {Object.keys(MODULE_ROUTES).map(moduleName => {
                    const Icon = MODULE_ICONS[moduleName];
                    const isSelected = selectedModule === moduleName;
                    const isSuggested = suggestedModule === moduleName && !userHasSetModule;

                    return (
                      <button
                        key={moduleName}
                        type="button"
                        onClick={() => handleModuleSelect(moduleName)}
                        className={`p-2 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                          isSelected
                            ? 'border-teal-500 bg-teal-50 text-teal-700'
                            : isSuggested
                            ? 'border-teal-300 bg-teal-50/50 text-teal-600'
                            : 'border-stone-200 text-slate-600 hover:border-stone-300 hover:bg-stone-50'
                        }`}
                        disabled={isSaving}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-xs font-medium">{moduleName}</span>
                      </button>
                    );
                  })}
                </div>

                {/* None and Custom options */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleModuleSelect('None')}
                    className={`p-2 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                      selectedModule === null && !showCustomLinkInput
                        ? 'border-stone-400 bg-stone-50 text-stone-700'
                        : 'border-stone-200 text-slate-600 hover:border-stone-300 hover:bg-stone-50'
                    }`}
                    disabled={isSaving}
                  >
                    <Ban className="w-4 h-4" />
                    <span className="text-xs font-medium">None</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleModuleSelect('Custom')}
                    className={`p-2 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                      showCustomLinkInput
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-stone-200 text-slate-600 hover:border-stone-300 hover:bg-stone-50'
                    }`}
                    disabled={isSaving}
                  >
                    <Link className="w-4 h-4" />
                    <span className="text-xs font-medium">Custom Link</span>
                  </button>
                </div>

                {/* Custom link input */}
                {showCustomLinkInput && (
                  <div className="animate-in slide-in-from-top-1">
                    <input
                      type="url"
                      value={customModuleLink}
                      onChange={(e) => setCustomModuleLink(e.target.value)}
                      placeholder="e.g., https://studentaid.gov/h/apply-for-aid/fafsa"
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 text-sm"
                      disabled={isSaving}
                    />
                    <p className="text-xs text-slate-500 mt-1">Enter a URL to link this task to any external tool or page</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Optional quick fields - always available but subtle */}
          <div className={`grid grid-cols-2 gap-3 transition-opacity ${
            expandLevel === 'minimal' ? 'opacity-60' : 'opacity-100'
          }`}>
            {/* Estimated time */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Time needed</label>
              <select
                value={estimatedHours}
                onChange={(e) => handleHoursChange(parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 text-sm"
                disabled={isSaving}
              >
                <option value={0.25}>15 min</option>
                <option value={0.5}>30 min</option>
                <option value={0.75}>45 min</option>
                <option value={1}>1 hour</option>
                <option value={1.5}>1.5 hours</option>
                <option value={2}>2 hours</option>
                <option value={2.5}>2.5 hours</option>
                <option value={3}>3+ hours</option>
              </select>
            </div>

            {/* College selector */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">College</label>
              <select
                value={selectedCollege || ''}
                onChange={(e) => setSelectedCollege(e.target.value || null)}
                className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 text-sm"
                disabled={isSaving}
              >
                <option value="">General task</option>
                {colleges.map(college => (
                  <option key={college.id} value={college.id}>
                    {college.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Expand for more options hint */}
          {expandLevel === 'minimal' && taskTitle.length > 0 && (
            <p className="text-xs text-slate-500 text-center animate-in fade-in">
              Press <kbd className="px-1.5 py-0.5 bg-stone-100 rounded text-xs">Tab</kbd> for more options
            </p>
          )}
        </div>

        {/* Duplicate warning */}
        {showDuplicateWarning && (
          <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
            <p className="text-xs text-amber-800">
              Similar task may already exist. Continue anyway?
            </p>
          </div>
        )}

        {/* Smart suggestions */}
        {(suggestedCategory || suggestedModule) && mode === 'quick' && !isSaving && expandLevel !== 'minimal' && (
          <div className="flex items-center gap-2 p-2 bg-teal-50 border border-teal-200 rounded-lg animate-in slide-in-from-bottom-1">
            <Sparkles className="w-4 h-4 text-teal-600" />
            <p className="text-xs text-teal-800">
              AI suggests:
              {suggestedCategory && (
                <span className="font-medium"> {suggestedCategory} task</span>
              )}
              {suggestedHours && (
                <span className="font-medium"> • {suggestedHours}h</span>
              )}
              {suggestedModule && (
                <span className="font-medium"> • {suggestedModule} module</span>
              )}
            </p>
            <button
              type="button"
              onClick={handleApplyAllSuggestions}
              className="ml-auto text-xs text-teal-600 hover:text-teal-700 font-medium"
            >
              Apply All
            </button>
          </div>
        )}

        {/* Detailed mode fields */}
        {mode === 'detailed' && !isSaving && (
          <div className="space-y-3 pt-2 border-t border-stone-100">
            <div className="grid grid-cols-2 gap-2">
              {/* Category selector */}
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500"
                >
                  <option value="admin">Applications</option>
                  <option value="essay">Essays</option>
                  <option value="research">Research</option>
                  <option value="testing">Testing</option>
                  <option value="financial">Financial Aid</option>
                  <option value="profile">Profile</option>
                </select>
              </div>

              {/* Duration estimator */}
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Duration</label>
                <select
                  value={estimatedHours}
                  onChange={(e) => setEstimatedHours(parseFloat(e.target.value))}
                  className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500"
                >
                  <option value={0.25}>15 min</option>
                  <option value={0.5}>30 min</option>
                  <option value={0.75}>45 min</option>
                  <option value={1}>1 hour</option>
                  <option value={1.5}>1.5 hours</option>
                  <option value={2}>2 hours</option>
                  <option value={2.5}>2.5 hours</option>
                  <option value={3}>3 hours</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {/* College selector */}
              <div>
                <label className="text-xs text-slate-600 mb-1 block">College (optional)</label>
                <select
                  value={selectedCollege || ''}
                  onChange={(e) => setSelectedCollege(e.target.value || null)}
                  className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500"
                >
                  <option value="">General</option>
                  {colleges.map(college => (
                    <option key={college.id} value={college.id}>
                      {college.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Priority selector */}
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500"
                >
                  <option value="medium">Normal</option>
                  <option value="high">High</option>
                  <option value="low">Low</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Loading state with insight */}
        {isSaving && (
          <div className="py-3 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-teal-600 border-t-transparent mx-auto mb-2"></div>
            <p className="text-xs text-slate-600">💡 {loadingInsight}</p>
          </div>
        )}

        {/* Enhanced workload impact preview */}
        {!isSaving && (
          <div className="space-y-2">
            <div className={`p-3 rounded-xl ${getWorkloadColor()}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">
                    Current: {getDateWorkload().toFixed(1)}h → After adding: {(getDateWorkload() + (userHasSetHours ? estimatedHours : (suggestedHours || estimatedHours))).toFixed(1)}h
                  </div>
                  <div className="text-xs mt-1">
                    {getDateWorkload() + (userHasSetHours ? estimatedHours : (suggestedHours || estimatedHours)) <= 3 && (
                      <span className="text-emerald-700">✅ Light day - good for focus work</span>
                    )}
                    {getDateWorkload() + (userHasSetHours ? estimatedHours : (suggestedHours || estimatedHours)) > 3 && getDateWorkload() + (userHasSetHours ? estimatedHours : (suggestedHours || estimatedHours)) <= 6 && (
                      <span className="text-amber-700">⚡ Busy but manageable</span>
                    )}
                    {getDateWorkload() + (userHasSetHours ? estimatedHours : (suggestedHours || estimatedHours)) > 6 && (
                      <span className="text-red-700">⚠️ Consider moving some tasks to another day</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        {!isSaving && (
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setIsAdding(false);
                setMode('quick');
              }}
              className="flex-1 px-4 py-3 text-base font-medium text-slate-700 border-2 border-stone-200 rounded-xl hover:bg-stone-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 text-base font-medium text-white bg-teal-600 rounded-xl hover:bg-teal-700 transition-colors shadow-sm"
            >
              Add Task
            </button>
          </div>
        )}

        {/* Keyboard shortcuts hint */}
        {mode === 'quick' && !isSaving && expandLevel === 'minimal' && (
          <p className="text-xs text-slate-500 text-center pt-1">
            <kbd className="px-1.5 py-0.5 bg-stone-100 rounded text-xs">Enter</kbd> to add •
            <kbd className="px-1.5 py-0.5 bg-stone-100 rounded text-xs ml-1">Tab</kbd> for options
          </p>
        )}
      </form>
    </div>
  );
};

export default QuickTaskAdd;